import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, LogOut, Loader2, ShoppingBag } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  pending: 'Pendiente',
  confirmed: 'Confirmado',
  processing: 'En proceso',
  shipped: 'Enviado',
  delivered: 'Entregado',
  cancelled: 'Cancelado',
  refunded: 'Reembolsado',
};

const Profile = () => {
  const navigate = useNavigate();
  const { user, token, loading, isAuthenticated, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);

  const formatPrice = (price) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(price || 0);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const fetchOrders = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/api/auth/orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setOrdersLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchOrders();
    }
  }, [isAuthenticated, token, fetchOrders]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading || !isAuthenticated) {
    return (
      <div className="pt-32 pb-24 px-6 max-w-7xl mx-auto text-center" data-testid="profile-loading">
        <Loader2 className="w-10 h-10 mx-auto text-brand-cobalt animate-spin" />
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 px-6 md:px-12 max-w-4xl mx-auto" data-testid="profile-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl md:text-5xl tracking-tight font-light mb-2 text-brand-black">
            Mi Perfil
          </h1>
          <p className="text-brand-black/60">
            {user?.first_name} {user?.last_name}
          </p>
          <p className="text-brand-black/40 text-sm">{user?.email}</p>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 text-sm text-brand-black/60 hover:text-brand-magenta transition-colors px-4 py-2 border border-brand-black/20 rounded-lg"
          data-testid="logout-btn"
        >
          <LogOut size={16} />
          Cerrar sesión
        </button>
      </div>

      <h2 className="font-display text-xl md:text-2xl tracking-tight mb-4 text-brand-black flex items-center gap-2">
        <Package size={22} />
        Mis Pedidos
      </h2>

      {ordersLoading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 mx-auto text-brand-cobalt animate-spin" />
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-16 brand-card rounded-lg" data-testid="profile-no-orders">
          <ShoppingBag className="mx-auto h-12 w-12 text-brand-cobalt/20 mb-4" />
          <p className="text-brand-black/50">Aún no tienes pedidos</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <div
              key={order.id}
              className="brand-card rounded-lg p-4 md:p-6"
              data-testid={`order-card-${order.order_number}`}
            >
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div>
                  <p className="font-medium text-brand-black">{order.order_number}</p>
                  <p className="text-xs text-brand-black/40">{formatDate(order.created_at)}</p>
                </div>
                <span className="px-3 py-1 bg-brand-cobalt/10 text-brand-cobalt text-xs font-medium rounded-full">
                  {STATUS_LABELS[order.status] || order.status}
                </span>
              </div>

              <div className="flex gap-2 overflow-x-auto mb-3">
                {(order.items || []).map((item, idx) => (
                  <img
                    key={idx}
                    src={item.product_image}
                    alt={item.product_name}
                    className="w-14 h-14 object-cover rounded flex-shrink-0 bg-brand-black/5"
                  />
                ))}
              </div>

              {(order.promo_gifts || []).length > 0 && (
                <p className="text-xs text-brand-black/60 mb-3" data-testid={`order-gifts-${order.order_number}`}>
                  🎁 Regalo (10ml): {order.promo_gifts.map((g) => g.product_name).join(', ')}
                </p>
              )}

              <div className="flex items-center justify-between pt-3 border-t border-brand-black/10">
                <span className="text-sm text-brand-black/50">
                  {(order.items || []).length} {(order.items || []).length === 1 ? 'producto' : 'productos'}
                </span>
                <span className="font-display text-lg font-bold text-brand-black">
                  {formatPrice(order.total)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Profile;
